import {Box, Dialog, DialogTitle, IconButton, Stack} from "@mui/material";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import ReactJson, {OnCopyProps} from "react-json-view";
import {Container} from "@mui/system";
import {Close as CloseIcon} from "@mui/icons-material";
import CopyToClipboard from "copy-to-clipboard";
import {ListQuery, ListRequest} from "../../../app/api/types";
import {useStageListQuery} from "../../../app/api/api";
import {skipToken} from "@reduxjs/toolkit/dist/query";
import ExTable from "../../../components/ExTable/ExTable";
import {getDeviceHistoryColumnDefs, getDeviceHistoryFilterDefs} from "./helpers";
import {DeviceHistoryEntry, DeviceHistoryEntryField} from "../../../app/api/model/deviceHistory";
import {EetsDeviceHistoryEntry} from "../../../app/api/model/eetsDevice";

interface HistoryTableProps<T extends DeviceHistoryEntry | EetsDeviceHistoryEntry, U extends ListRequest> {
    additionalRequestParams: U;
    queryHook: ListQuery<T, U>;
    isEetsDevice?: boolean;
}

function HistoryTable<T extends DeviceHistoryEntry | EetsDeviceHistoryEntry, U extends ListRequest>({
    additionalRequestParams,
    queryHook,
    isEetsDevice = false,
}: HistoryTableProps<T, U>) {
    const {t} = useTranslation();
    const [viewJson, setViewJson] = useState<object | undefined>();
    const [viewText, setViewText] = useState<string | undefined>();
    const {data: stageListResponse} = useStageListQuery(!isEetsDevice ? {} : skipToken);

    const onJsonViewerClose = () => setViewJson(undefined);
    const onTextViewerClose = () => setViewText(undefined);
    const handleCopyToClipboard = (copy: OnCopyProps) => {
        CopyToClipboard(JSON.stringify(copy.src, null, "\t"));
    };

    return (
        <>
            <ExTable<T, U>
                queryHook={queryHook}
                additionalRequestParams={additionalRequestParams}
                columnDefs={getDeviceHistoryColumnDefs<T>(t, setViewJson, setViewText, isEetsDevice)}
                filterDefs={getDeviceHistoryFilterDefs<T>(t, isEetsDevice, stageListResponse?.payload)}
                title={t("generic_history")}
                selectableRows={false}
                keyFieldName={DeviceHistoryEntryField.TIMESTAMP}
                compact={true}
            />
            {viewJson && (
                <Dialog onClose={onJsonViewerClose} open={true}>
                    <DialogTitle sx={{minWidth: "600px"}}>
                        <Stack direction="row" justifyContent="space-between" alignContent="center">
                            <Container sx={{margin: "auto"}} component={"div"}>
                                {t("generic_viewJson")}
                            </Container>
                            <IconButton onClick={onJsonViewerClose}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <ReactJson src={viewJson} theme={"ocean"} enableClipboard={handleCopyToClipboard} />
                </Dialog>
            )}
            {viewText && (
                <Dialog onClose={onTextViewerClose} open={true}>
                    <DialogTitle>
                        <Stack direction="row" justifyContent="space-between" alignContent="center">
                            <Container sx={{margin: "auto"}} component={"div"}>
                                {t("generic_viewText")}
                            </Container>
                            <IconButton onClick={onTextViewerClose}>
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </DialogTitle>
                    <Box
                        sx={{
                            p: 2,
                            bgcolor: "#eee",
                            color: "#070",
                            fontFamily: "monospace",
                            whiteSpace: "pre",
                            mx: "auto",
                            overflow: "auto",
                        }}
                    >
                        {viewText}
                    </Box>
                </Dialog>
            )}
        </>
    );
}

export default HistoryTable;
